import { batch } from 'react-redux';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components';
import {
  IRowMeta,
  removeErrorAction,
  removeRowAction,
} from '../../../../redux/slices/oldBuilder';
import DragHandle from '../../DragHandle';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  builderSelectedOptionsSelector,
  currentStepSelector,
  stepOrdersSelector,
  stepsSelector,
} from '../../../../redux/selectors/builder';
import {
  FunnelOrderKey,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { HighlightedEntity } from '../../../HighlightingContext';
import { BuilderRowActions } from '../../BuilderActions';
import BuilderStepOptions from '../BuilderStepOptions';
import Partition from '../../Partition';
import { assertCondition } from '../../../../utils/types';
import { MAX_STEP_EXCLUDED_EVENTS_COUNT } from '../../../../config/constants';
import { useActionsVisibilityContext } from '../../../ActionsVisibilityContext';
import { theme } from '../../../../styles/theme';
import BadgeRound from '../../../BadgeRound';
import { SquareRemoveButton } from '../../../Button';

interface IProps {
  rowMeta: IRowMeta;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  onRemoveMouseEnter?: () => void;
  onRemoveMouseLeave?: () => void;
}

const Container = styled(HighlightedEntity)`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding-right: 16px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: ${theme.colors.tableHeader};
`;

const DragHandleContainer = styled.div`
  flex: 0 0 auto;
  width: 16px;
`;

const StepIndex = styled(BadgeRound)`
  margin-right: 8px;
`;

const StepOptionsContent = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
`;

const BuilderStepHeader = ({
  rowMeta,
  dragHandleProps,
  onRemoveMouseEnter,
  onRemoveMouseLeave,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { actionsVisible } = useActionsVisibilityContext();

  const { rowIndex } = rowMeta;

  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );

  assertCondition(selectedOptions?.type === QueryType.Funnel);

  const { funnelOrder: selectedFunnelOrder } = selectedOptions;

  const steps = useAppSelector(stepsSelector);

  const currentStep = useAppSelector((state) =>
    currentStepSelector(state, rowIndex),
  );

  const { showPartition, negativeSteps } = currentStep;

  const currentStepFunnelOrder = useAppSelector(stepOrdersSelector(rowMeta));

  const allowExcludeEvents =
    (selectedFunnelOrder === FunnelOrderKey.Sequence || // global order type is SEQUENCE
      currentStepFunnelOrder === FunnelOrderKey.Sequence || // ... or current step order is SEQUENCE
      // first step does not have custom step order. Kobol allows negative step in CUSTOM order for the first step
      (rowIndex === 0 && selectedFunnelOrder === FunnelOrderKey.Custom)) &&
    negativeSteps.length < MAX_STEP_EXCLUDED_EVENTS_COUNT;

  // allow step partition when index > 0 && global conversion window && order !== ANY
  const allowPartition =
    rowIndex > 0 && selectedFunnelOrder !== FunnelOrderKey.Any;

  const handleRemoveStep = () => {
    batch(() => {
      dispatch(removeErrorAction({ rowMeta, errorKey: '', clearRow: true }));
      dispatch(removeRowAction(rowMeta));
    });

    if (onRemoveMouseLeave) {
      onRemoveMouseLeave();
    }
  };

  const canRemoveStep = steps.length > 2;

  return (
    <Container>
      <DragHandleContainer>
        {dragHandleProps && (
          <DragHandle
            dragHandleProps={dragHandleProps}
            visible={actionsVisible}
          />
        )}
      </DragHandleContainer>
      <StepIndex size="regular">{String(rowIndex + 1)}</StepIndex>
      <StepOptionsContent>
        <BuilderStepOptions
          rowMeta={rowMeta}
          allowExcludeEvents={allowExcludeEvents}
          allowPartition={allowPartition}
          disablePartition={showPartition}
        />
        {showPartition && allowPartition && (
          <Partition queryType={QueryType.Funnel} rowMeta={rowMeta} />
        )}
      </StepOptionsContent>
      {actionsVisible && canRemoveStep && (
        <BuilderRowActions>
          <SquareRemoveButton
            content={'Remove a step from the conversion funnel.'}
            onMouseEnter={onRemoveMouseEnter}
            onMouseLeave={onRemoveMouseLeave}
            onClick={handleRemoveStep}
          />
        </BuilderRowActions>
      )}
    </Container>
  );
};

export default BuilderStepHeader;
